import { useState } from "react";
import axios from "axios";
import { Lock, Warning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Super Password Dialog — second-factor confirmation for destructive admin
 * actions (delete tournament/player/match, force-wipe groups, ELO repair
 * apply, rollback, etc.).
 *
 * Usage 1 (controlled): pass `open` + `onConfirm(password)` + `onCancel`.
 * Usage 2 (imperative): import { promptSuperPassword } and `await` it; it
 * mounts itself and resolves with the password (string) or null on cancel.
 *
 * The password is sent via the `X-Super-Password` HTTP header; backend
 * checks against env var `SUPER_PASSWORD` using a constant-time compare.
 */
export default function SuperPasswordDialog({
  open, onConfirm, onCancel,
  title = "Confirm destructive action",
  description = "This action will permanently delete or modify data. Enter the super password to continue.",
  actionLabel = "Confirm",
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e?.preventDefault();
    if (!password) {
      setError("Super password is required");
      return;
    }
    setError("");
    onConfirm?.(password);
    setPassword("");
  };

  const cancel = () => {
    setPassword("");
    setError("");
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
      <DialogContent className="bg-card border-destructive/40 max-w-md" data-testid="super-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Warning className="w-5 h-5" weight="fill" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" weight="fill" />
              Super password
            </label>
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="bg-background border-border"
              data-testid="super-password-input"
            />
            {error && <p className="text-xs text-destructive" data-testid="super-password-error">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={cancel} data-testid="super-password-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="super-password-confirm-btn"
            >
              <Lock className="w-4 h-4 mr-1.5" weight="fill" />
              {actionLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Imperative API ----------
//
// Mounts a temporary dialog and resolves with the entered password (string)
// when the admin confirms, or null when they cancel. Lets callers write
// straightforward async flows like:
//
//   const pw = await promptSuperPassword({ title: "Delete this tournament?" });
//   if (!pw) return;
//   await axios.delete(url, { headers: { "X-Super-Password": pw } });
//
import { createRoot } from "react-dom/client";

export function promptSuperPassword(opts = {}) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);

    const cleanup = () => {
      try { root.unmount(); } catch { /* noop */ }
      try { host.remove(); } catch { /* noop */ }
    };

    const handleConfirm = (pw) => {
      cleanup();
      resolve(pw);
    };
    const handleCancel = () => {
      cleanup();
      resolve(null);
    };

    root.render(
      <SuperPasswordDialog
        open
        title={opts.title}
        description={opts.description}
        actionLabel={opts.actionLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  });
}

/**
 * Make a destructive API call gated by the super password.
 * Returns the axios response, or `null` if the admin cancelled the prompt.
 *
 * Example:
 *   await callWithSuperPassword(
 *     ({ headers }) => axios.delete(`${API}/players/${id}`, { headers, withCredentials: true }),
 *     { title: "Delete player?" }
 *   );
 */
export async function callWithSuperPassword(makeRequest, opts = {}) {
  const pw = await promptSuperPassword(opts);
  if (!pw) return null;
  const headers = { "X-Super-Password": pw };
  try {
    return await makeRequest({ headers });
  } catch (err) {
    // Surface 403 with a friendlier message
    if (err?.response?.status === 403 && /super password/i.test(err.response?.data?.detail || "")) {
      const { toast } = await import("sonner");
      toast.error("Wrong super password — action blocked");
    }
    throw err;
  }
}

// Attach a global request interceptor: if a destructive call is sent without
// the X-Super-Password header AND the server returns 403 with the
// "Super password required" message, the wrapper above already handles UI.
// This block is a no-op placeholder kept for clarity / future hooks.
export const __noop = axios;
